import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useCategorySearch,
  useTagGroupSearch,
} from "@/hooks/use-taxonomy-search";
import type { Category, TagGroup, TagOption } from "@/lib/schema";
import { Check, ChevronDown, LayoutGrid, Search } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { BuilderSection } from "./builder-section";

interface IntentTaggingSectionProps {
  categories: Category[];
  categoryId: string;
  onCategoryChange: (id: string) => void;
  tagGroups: TagGroup[];
  getGroupOptions: (groupId: string) => TagOption[];
  selectedTagOptionIds: string[];
  onToggleTagOption: (id: string) => void;
  isReadOnly?: boolean;
  stepNumber?: number;
  children?: ReactNode;
}

export function IntentTaggingSection({
  categories,
  categoryId,
  onCategoryChange,
  tagGroups,
  getGroupOptions,
  selectedTagOptionIds,
  onToggleTagOption,
  isReadOnly,
  stepNumber,
  children,
}: IntentTaggingSectionProps) {
  const selectedTagCount = selectedTagOptionIds.length;
  const liveCategories = categories.filter((c) => c.status === "active");
  const liveTagGroups = tagGroups.filter((g) => g.status === "active");
  const moodGroup = useMemo(
    () => liveTagGroups.find((g) => g.type === "mood"),
    [liveTagGroups],
  );
  const feelingGroup = useMemo(
    () => liveTagGroups.find((g) => g.type === "feeling"),
    [liveTagGroups],
  );

  const selectedMoodIds = useMemo(() => {
    if (!moodGroup) return [];
    const moodOpts = getGroupOptions(moodGroup.id);
    return moodOpts
      .filter((o) => selectedTagOptionIds.includes(o.id))
      .map((o) => o.id);
  }, [moodGroup, getGroupOptions, selectedTagOptionIds]);

  const getFilteredFeelingOptions = useCallback(
    (options: TagOption[]) => {
      if (selectedMoodIds.length === 0) return [];
      return options
        .filter(
          (opt) =>
            opt.parentTagOptionIds &&
            opt.parentTagOptionIds.some((pid) => selectedMoodIds.includes(pid)),
        )
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    [selectedMoodIds],
  );

  useEffect(() => {
    if (!feelingGroup) return;
    const feelingOpts = getGroupOptions(feelingGroup.id).filter(
      (o) => o.status === "active",
    );
    const selectedFeelings = feelingOpts.filter((o) =>
      selectedTagOptionIds.includes(o.id),
    );
    const orphanedFeelings = selectedFeelings.filter(
      (f) =>
        !f.parentTagOptionIds ||
        !f.parentTagOptionIds.some((pid) => selectedMoodIds.includes(pid)),
    );
    orphanedFeelings.forEach((f) => onToggleTagOption(f.id));
  }, [
    selectedMoodIds,
    feelingGroup,
    getGroupOptions,
    selectedTagOptionIds,
    onToggleTagOption,
  ]);

  const [catOpen, setCatOpen] = useState(false);

  const categorySearch = useCategorySearch(liveCategories);
  const tagSearch = useTagGroupSearch();

  const selectedCategory = liveCategories.find((c) => c.id === categoryId);

  const completionParts: string[] = [];
  if (categoryId) completionParts.push("category");
  if (selectedTagCount > 0)
    completionParts.push(
      `${selectedTagCount} tag${selectedTagCount !== 1 ? "s" : ""}`,
    );
  const badgeText =
    completionParts.length > 0 ? completionParts.join(", ") : undefined;

  const showValidation = !categoryId || selectedTagCount === 0;

  return (
    <BuilderSection
      title="Classification"
      description="Define how this content is categorized and discovered"
      icon={LayoutGrid}
      stepNumber={stepNumber}
      badge={badgeText}
      disabled={isReadOnly}
    >
      <div className="space-y-6">
        {showValidation && (
          <div
            className="rounded-md border border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 p-3"
            data-testid="classification-validation"
          >
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {!categoryId && !selectedTagCount
                ? "Select a category and add personalization tags to continue."
                : !categoryId
                  ? "Select a category to continue."
                  : "Add at least 1 personalization tag to continue."}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Category <span className="text-destructive">*</span>
          </Label>
          <Popover open={catOpen} onOpenChange={setCatOpen} modal={false}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal"
                disabled={isReadOnly}
                data-testid="select-category"
              >
                {selectedCategory
                  ? selectedCategory.title
                  : "Select a category"}
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
              side="bottom"
              avoidCollisions
              collisionPadding={8}
            >
              <div className="p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search categories..."
                    value={categorySearch.query}
                    onChange={(e) => categorySearch.setQuery(e.target.value)}
                    className="pl-8 h-8 text-sm"
                    data-testid="input-search-category"
                  />
                </div>
              </div>
              <ScrollArea className="h-[240px]">
                <div className="p-1">
                  {categorySearch.filtered.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No categories found
                    </p>
                  ) : (
                    categorySearch.filtered.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        className="flex items-center gap-3 w-full rounded-md px-2 py-2 text-sm hover-elevate cursor-pointer text-left"
                        onClick={() => {
                          onCategoryChange(cat.id);
                          setCatOpen(false);
                          categorySearch.setQuery("");
                        }}
                        data-testid={`category-option-${cat.id}`}
                      >
                        <div
                          className={`flex items-center justify-center h-4 w-4 rounded-full border ${
                            categoryId === cat.id
                              ? "border-primary bg-primary"
                              : "border-muted-foreground/40"
                          }`}
                        >
                          {categoryId === cat.id && (
                            <Check className="h-3 w-3 text-primary-foreground" />
                          )}
                        </div>
                        <span className="text-foreground">{cat.title}</span>
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-sm font-medium">
              Personalization Tags <span className="text-destructive">*</span>
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Select tags to help match this content to users
            </p>
          </div>
          {liveTagGroups.map((group, groupIndex) => {
            const allOptions = getGroupOptions(group.id).filter(
              (o) => o.status === "active",
            );
            if (allOptions.length === 0) return null;

            const isFeelingType = group.type === "feeling";
            const needsMoodFirst =
              isFeelingType && selectedMoodIds.length === 0;
            const visibleOptions = isFeelingType
              ? getFilteredFeelingOptions(allOptions)
              : allOptions;
            const filteredOptions = tagSearch.getFiltered(
              group.id,
              visibleOptions,
            );
            const selectedInGroup = visibleOptions.filter((o) =>
              selectedTagOptionIds.includes(o.id),
            ).length;
            const defaultExpanded = groupIndex < 2;

            return (
              <TagGroupCollapsible
                key={group.id}
                group={group}
                options={filteredOptions}
                totalOptions={visibleOptions.length}
                selectedCount={selectedInGroup}
                selectedTagOptionIds={selectedTagOptionIds}
                onToggleTagOption={onToggleTagOption}
                searchQuery={tagSearch.getQuery(group.id)}
                onSearchChange={(q) => tagSearch.setQuery(group.id, q)}
                defaultOpen={defaultExpanded}
                isReadOnly={isReadOnly}
                emptyMessage={
                  needsMoodFirst
                    ? "Select a mood first to see available feelings"
                    : undefined
                }
              />
            );
          })}
        </div>

        {children}
      </div>
    </BuilderSection>
  );
}

interface TagGroupCollapsibleProps {
  group: TagGroup;
  options: TagOption[];
  totalOptions: number;
  selectedCount: number;
  selectedTagOptionIds: string[];
  onToggleTagOption: (id: string) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  defaultOpen: boolean;
  isReadOnly?: boolean;
  emptyMessage?: string;
}

function TagGroupCollapsible({
  group,
  options,
  totalOptions,
  selectedCount,
  selectedTagOptionIds,
  onToggleTagOption,
  searchQuery,
  onSearchChange,
  defaultOpen,
  isReadOnly,
  emptyMessage,
}: TagGroupCollapsibleProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const visibleSelectedCount = options.filter((o) =>
    selectedTagOptionIds.includes(o.id),
  ).length;
  const allVisibleSelected =
    options.length > 0 && visibleSelectedCount === options.length;

  const handleBulkToggle = () => {
    if (allVisibleSelected) {
      options.forEach((o) => {
        if (selectedTagOptionIds.includes(o.id)) onToggleTagOption(o.id);
      });
    } else {
      options.forEach((o) => {
        if (!selectedTagOptionIds.includes(o.id)) onToggleTagOption(o.id);
      });
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="rounded-xl border border-input/50">
        <CollapsibleTrigger asChild>
          <div
            className="flex items-center justify-between gap-2 px-3 py-2.5 cursor-pointer select-none"
            data-testid={`tag-group-trigger-${group.id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="text-sm font-medium text-foreground truncate min-w-0"
                title={group.name}
              >
                {group.name}
              </span>
              {selectedCount > 0 && (
                <Badge
                  variant="default"
                  className="text-[10px] shrink-0 no-default-hover-elevate no-default-active-elevate"
                >
                  {selectedCount}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                {totalOptions} options
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {!isReadOnly && options.length > 0 && (
                <label
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border cursor-pointer hover-elevate text-xs whitespace-nowrap ${
                    allVisibleSelected
                      ? "border-primary bg-primary/5"
                      : "border-border"
                  }`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`bulk-toggle-tags-${group.id}`}
                >
                  <Checkbox
                    checked={
                      visibleSelectedCount === 0
                        ? false
                        : allVisibleSelected
                          ? true
                          : "indeterminate"
                    }
                    onCheckedChange={handleBulkToggle}
                    className="shrink-0"
                  />
                  <span className="text-foreground">
                    {allVisibleSelected ? "Deselect all" : "Select all"}
                  </span>
                </label>
              )}
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t px-3 pb-3 pt-2 space-y-2">
            {totalOptions > 6 && (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder={`Search ${group.name.toLowerCase()}...`}
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-8 h-8 py-1 text-sm"
                  data-testid={`input-search-tags-${group.id}`}
                />
              </div>
            )}
            {emptyMessage && totalOptions === 0 ? (
              <div
                className="rounded-md border border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 p-3"
                data-testid="feeling-mood-prompt"
              >
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  {emptyMessage}
                </p>
              </div>
            ) : options.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">
                No matching tags
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {options.map((opt) => {
                  const isSelected = selectedTagOptionIds.includes(opt.id);
                  return (
                    <label
                      key={opt.id}
                      className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md border cursor-pointer hover-elevate text-sm whitespace-nowrap ${
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      } ${isReadOnly ? "opacity-60 pointer-events-none" : ""}`}
                      data-testid={`tag-option-${opt.id}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleTagOption(opt.id)}
                        disabled={isReadOnly}
                        className="shrink-0"
                      />
                      <span className="text-foreground">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
